<template>
  <AuthenticatedLayout activeMenuItem='assessment'>
    <LoadingLayout :isLoading='assessmentRead.isLoading()' class='h-dvh'>
      <form v-if='assessmentRead.hasData()' @submit.prevent='submit'>
        <AssessmentInterviewToolbar :state='state' :actions='actions' />
        <AssessmentInterviewDrawer :state='state' />
        <aside class="fixed inset-y-0 top-16 left-72 hidden w-96 overflow-y-auto border-r border-gray-200 xl:block">
          <AssessmentInterviewSidebar :state='state' />
        </aside>
        <div class="xl:pl-96 flex" style='min-height: calc(100dvh - 4rem);'>
          <PaddedLayout class='bg-gray-100 flex-1'>
            <button v-if='state.response' class='mb-4 flex items-center gap-2 text-sm xl:hidden' @click='openDrawer'>
              <Bars3BottomLeftIcon class='w-5 h-5' /> Objectives
            </button>
            <AssessmentInterviewResponse :state='state' v-if='state.response' />
            <div class='h-full border-dashed border-2 rounded-lg border-gray-300 flex items-center justify-center text-gray-400 text-xl text-center p-4 flex-col gap-4' v-else>
              Choose an objective from the list to get started.
              <button type="button" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600 xl:hidden" @click='openDrawer'>Show Objectives</button>
            </div>
          </PaddedLayout>
        </div>
      </form>
      <PaddedLayout v-if='assessmentRead.hasError()'>
        <ErrorNotice :error='assessmentRead.getError()' />
      </PaddedLayout>
    </LoadingLayout>

    <ProgressAlert :isPresenting='assessmentWrite.isLoading()'>
      <template #title>
        Processing...
      </template>
    </ProgressAlert>

    <ErrorAlert :controller='errorAlertController' />
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, reactive, watch } from 'vue';

import { Bars3BottomLeftIcon } from '@heroicons/vue/24/outline';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ErrorNotice from '@/components/notices/error-notice.vue';

import AssessmentInterviewToolbar from '@/components/assessment-interview/assessment-interview-toolbar.vue';
import AssessmentInterviewSidebar from '@/components/assessment-interview/assessment-interview-sidebar.vue';
import AssessmentInterviewResponse from '@/components/assessment-interview/assessment-interview-response.vue';
import AssessmentInterviewDrawer from '@/components/assessment-interview/assessment-interview-drawer.vue';

import ProgressAlert from '@/components/alerts/progress-alert.vue';
import ErrorAlert from '@/components/alerts/error-alert.vue';

import SuccessToast from '@/components/toasts/success-toast.vue';

import { usePayloadAction, doPayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';
import { useAlertController } from '@/kernel/controllers/alert-controller';
import { getOrganisationId } from '@/kernel/session';
import { useToastManager } from '@/kernel/managers/toast-manager';
import { debounce } from '@/kernel/debounce';

import { pushRoute } from '@/router/index.js';

const props = defineProps({
  id: String
});

const state = reactive({
  assessment: null,
  assessmentInterviewId: null,
  status: null,
  responses: [],
  response: null,
  drawerOpen: false
});

const assessmentCollection = getCollection('assessment');
const themeCollection = getCollection('theme');

const errorAlertController = useAlertController();

const toastManager = useToastManager();
const toastController = toastManager.registerToast('assessment-save-changes', SuccessToast);

const assessmentRead = usePayloadAction('GET', assessmentCollection.getApiObjectEndpoint(props.id));
const assessmentWrite = usePayloadAction('PATCH', assessmentCollection.getApiObjectEndpoint(props.id));

const openDrawer = () => {
  state.drawerOpen = true;
}

const toggleStatus = () => {
  setStatus(state.status === 'draft' ? 'complete' : 'draft');
}

const setStatus = async (status) => {
  try {
    await assessmentWrite.doAction({
      body: {
        status
      }
    });

    toastController.present({data: {title: `${assessmentCollection.getSingleLabel()} successfully set to ${status}.`}});

    state.status = status;

    if (status === 'complete') {
      pushRoute({
        path: '/'
      });
    }
  } catch(e) {
    errorAlertController.present({
      data: {error: e}
    });
  }
}

const autosave = async (showToast = true) => {
  try {
    const responses = state.responses.map((response) => {
      const { objectiveId, score, notes } = response;
      return { objectiveId, score, notes, test: true };
    });

    if (!state.assessmentInterviewId) {
      const result = await doPayloadQuery('POST', assessmentCollection.getApiInterviewIndexEndpoint(), {
        body: {
          organisation: getOrganisationId(),
          assessment: state.assessment.id,
          responses
        }
      });

      state.assessmentInterviewId = result.doc.id;
    } else if (state.assessmentInterviewId) {
      await doPayloadQuery('PATCH', assessmentCollection.getApiInterviewObjectEndpoint(state.assessmentInterviewId), {
        body: {
          responses
        }
      });
    }

    if (showToast) {
      toastController.present({data: {title: `${assessmentCollection.getSingleLabel()} draft successfully saved.`}});
    }

  } catch(e) {
    errorAlertController.present({
      data: {error: e}
    });
  }
}

const loadPage = async () => {
  const data = await assessmentRead.doAction({params: {depth: 4}});

  state.assessment = data;
  state.status = data.status;

  const responses = (data.latestAssessmentInterview?.responses || data.responses || []).map(response => reactive(response));

  data.assessmentTemplate.objectives.forEach((objective) => {
    const exists = responses.find(response => response.objectiveId === objective.id);

    if (!exists) {
      responses.push(reactive({
        objectiveId: objective.id,
        score: null,
        notes: null
      }));
    }
  });

  responses.forEach((response) => {
    response.objective = data.assessmentTemplate.objectives.find(objective => response.objectiveId === objective.id);
  });

  const themes = {};

  responses.forEach((response) => {
    const themeId = themeCollection.getId(response.objective.theme);

    if (!themes[themeId]) {
      themes[themeId] = {...response.objective.theme, responses: []};
    }

    themes[themeId].responses.push(response);
  });

  state.responses = responses;
  state.themes = Object.values(themes);

  state.responses.forEach((response) => {
    const debounced = debounce(() => {
      autosave(false);
    }, 1000);

    watch(response, () => {
      if (state.status !== 'draft') {
        return;
      }

      debounced();
    });
  });
}

const actions = {
  autosave,
  toggleStatus
}

loadPage();

</script>
